import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Button, RouteShell, TabBar } from "@/design-system";
import { Breadcrumb } from "@/components/chrome/PageChrome";
import { IssueDetailHead } from "@/components/issues/IssueDetailHead";
import { IssueDetailOverview } from "@/components/issues/IssueDetailOverview";
import { IssueDetailSidebar } from "@/components/issues/IssueDetailSidebar";
import { IssueLifecycleBanner } from "@/components/issues/IssueLifecycleBanner";
import { IssueOccurrencesList } from "@/components/issues/IssueOccurrencesList";
import { track } from "@/lib/analytics";
import type { IssueSeverity } from "@/api/issues";
import {
  getIssue,
  listIssueFindings,
  reopenIssue,
  resolveIssue,
  suppressIssue,
  unsuppressIssue,
  type IssueDetail,
} from "@/api/issues";

const ISSUE_SEVERITIES: readonly IssueSeverity[] = [
  "critical",
  "high",
  "medium",
  "low",
  "info",
];

// `Issue.severity` is typed `string` (platform contract). Narrow it to the
// analytics union, defaulting unknown values to "info".
function asIssueSeverity(s: string): IssueSeverity {
  return (ISSUE_SEVERITIES as readonly string[]).includes(s)
    ? (s as IssueSeverity)
    : "info";
}

const searchSchema = z.object({
  tab: z.enum(["overview", "findings"]).optional().catch(undefined),
});

export const Route = createFileRoute("/targets/$targetId/issues/$issueId")({
  validateSearch: searchSchema,
  component: IssueDetailPage,
});

function IssueDetailPage() {
  const { targetId, issueId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/targets/$targetId/issues/$issueId" });
  const qc = useQueryClient();

  const tab = search.tab ?? "overview";

  const issueQ = useQuery({
    queryKey: ["issue", issueId],
    queryFn: ({ signal }) => getIssue(issueId, signal),
  });
  const findingsQ = useQuery({
    queryKey: ["issue", issueId, "findings"],
    queryFn: ({ signal }) => listIssueFindings(issueId, signal),
    enabled: issueQ.data != null,
  });

  const findings = findingsQ.data?.findings ?? [];
  const issue = issueQ.data;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["issue", issueId] });
    void qc.invalidateQueries({ queryKey: ["issues", "list"] });
  };

  const trackStatusChange = (
    action: "resolve" | "reopen" | "suppress" | "unsuppress",
  ) => {
    const severity = issue?.severity;
    if (severity) {
      track({
        event: "issue_status_changed",
        action,
        severity: asIssueSeverity(severity),
      });
    }
  };

  const resolveMu = useMutation({
    mutationFn: () => resolveIssue(issueId),
    onSuccess: () => {
      trackStatusChange("resolve");
      invalidate();
    },
  });
  const reopenMu = useMutation({
    mutationFn: () => reopenIssue(issueId),
    onSuccess: () => {
      trackStatusChange("reopen");
      invalidate();
    },
  });
  const suppressMu = useMutation({
    mutationFn: () => suppressIssue(issueId, {}),
    onSuccess: () => {
      trackStatusChange("suppress");
      invalidate();
    },
  });
  const unsuppressMu = useMutation({
    mutationFn: () => unsuppressIssue(issueId),
    onSuccess: () => {
      trackStatusChange("unsuppress");
      invalidate();
    },
  });

  const busy =
    resolveMu.isPending ||
    reopenMu.isPending ||
    suppressMu.isPending ||
    unsuppressMu.isPending;

  const setTab = (next: "overview" | "findings") => {
    if (next !== tab) track({ event: "issue_tab_viewed", tab: next });
    void navigate({
      search: () => ({ tab: next === "overview" ? undefined : next }),
      replace: true,
    });
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="shrink-0">
        <div className="flex flex-row items-center gap-2 px-4 sm:px-7 h-13 border-b border-border-1 bg-bg-1">
          <Breadcrumb
            crumbs={[
              { label: "Servers", to: "/targets" },
              {
                label: issue?.targetDisplayName ?? "…",
                to: "/targets/$targetId",
                params: { targetId },
              },
              {
                label: "Issues",
                to: "/targets/$targetId",
                params: { targetId },
                search: { tab: "issues" },
              },
              { label: issue?.displayId ?? "…" },
            ]}
          />
        </div>
        {issue ? (
          <div className="flex flex-row items-start justify-between gap-6 px-4 sm:px-7 pt-6 pb-5 bg-bg-2 flex-wrap">
            <IssueDetailHead issue={issue} />
            <ActionButtons
              issue={issue}
              onResolve={() => resolveMu.mutate()}
              onReopen={() => reopenMu.mutate()}
              onSuppress={() => suppressMu.mutate()}
              onUnsuppress={() => unsuppressMu.mutate()}
              busy={busy}
            />
          </div>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-7 py-6">
        <RouteShell isPending={issueQ.isPending} error={issueQ.error}>
          {issue ? (
            <div className="flex flex-row items-start gap-7">
              <div className="flex flex-1 min-w-0 flex-col gap-5">
                <IssueLifecycleBanner issue={issue} />
                <TabBar
                  items={[
                    { key: "overview", label: "Overview" },
                    {
                      key: "findings",
                      label: "Findings",
                      count: issue.findingCount,
                    },
                  ]}
                  active={tab}
                  onChange={setTab}
                />
                {tab === "overview" ? (
                  <IssueDetailOverview issue={issue} findings={findings} />
                ) : (
                  <RouteShell
                    isPending={findingsQ.isPending}
                    error={findingsQ.error}
                  >
                    <IssueOccurrencesList findings={findings} />
                  </RouteShell>
                )}
              </div>
              <aside className="w-76 shrink-0">
                <IssueDetailSidebar issue={issue} />
              </aside>
            </div>
          ) : null}
        </RouteShell>
      </div>
    </div>
  );
}

interface ActionButtonsProps {
  issue: IssueDetail;
  onResolve: () => void;
  onReopen: () => void;
  onSuppress: () => void;
  onUnsuppress: () => void;
  busy: boolean;
}

function ActionButtons({
  issue,
  onResolve,
  onReopen,
  onSuppress,
  onUnsuppress,
  busy,
}: ActionButtonsProps) {
  if (issue.status === "open") {
    return (
      <div className="flex flex-row gap-2 shrink-0">
        <Button variant="secondary" size="sm" onClick={onSuppress} disabled={busy}>
          Suppress
        </Button>
        <Button variant="primary" size="sm" onClick={onResolve} disabled={busy}>
          Resolve
        </Button>
      </div>
    );
  }
  if (issue.status === "suppressed") {
    return (
      <Button variant="secondary" size="sm" onClick={onUnsuppress} disabled={busy}>
        Unsuppress
      </Button>
    );
  }
  return (
    <Button variant="secondary" size="sm" onClick={onReopen} disabled={busy}>
      Reopen
    </Button>
  );
}
